import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Timeline,
  Assignment,
  AttachMoney,
  TrendingUp,
  AccountBalance,
  EmojiEvents,
} from '@mui/icons-material';

const visaTypes = [
  {
    title: 'Innovator Visa',
    description: 'For experienced entrepreneurs with innovative business ideas',
    requirements: [
      'Innovative business plan',
      'Minimum £50,000 investment funds',
      'Endorsement from approved body',
      'English language requirement',
    ],
  },
  {
    title: 'Start-up Visa',
    description: 'For first-time entrepreneurs setting up in the UK',
    requirements: [
      'Innovative business idea',
      'Endorsement from approved body',
      'English language requirement',
      'Sufficient maintenance funds',
    ],
  },
  {
    title: 'Global Business Mobility',
    description: 'For overseas businesses establishing UK presence',
    requirements: [
      'UK sponsor license',
      'Overseas business link',
      'Minimum skill level',
      'Salary requirement',
    ],
  },
  {
    title: 'Representative of Overseas Business',
    description: 'For senior employees establishing UK branch',
    requirements: [
      'Senior position in company',
      'First UK establishment',
      'Genuine business expansion',
      'English language ability',
    ],
  },
];

const benefits = [
  'Establish business presence in the UK',
  'Access to UK market and opportunities',
  'Bring family members',
  'Path to settlement',
  'Access to skilled workforce',
  'World-class business infrastructure',
];

const process = [
  {
    title: 'Business Assessment',
    description: 'Evaluate your business plan and visa options',
  },
  {
    title: 'Endorsement Support',
    description: 'Guidance with endorsing body requirements',
  },
  {
    title: 'Document Preparation',
    description: 'Assistance with business and personal documentation',
  },
  {
    title: 'Application Filing',
    description: 'Professional support with visa application',
  },
  {
    title: 'Business Setup',
    description: 'Guidance on establishing your business in the UK',
  },
];

const BusinessVisa = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Business sx={{ fontSize: 64, mb: 3, color: theme.palette.secondary.light }} />
              <Typography variant="h1" gutterBottom>
                UK Business Visa Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Your pathway to business success in the United Kingdom
              </Typography>
              <Button
                component={RouterLink}
                to="/consultation"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mr: 2 }}
              >
                Free Consultation
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overview Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom>
              Business in the UK
            </Typography>
            <Typography variant="body1" paragraph>
              The UK offers various business visa routes for entrepreneurs and companies looking to establish or expand their presence in the United Kingdom. Our expert team provides comprehensive support to help you navigate the complex business immigration system.
            </Typography>
            <Typography variant="body1">
              Whether you're an innovative entrepreneur, a startup founder, or representing an overseas business, we'll guide you through every step of the visa application process and business establishment.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Key Benefits
                </Typography>
                <List>
                  {benefits.map((benefit) => (
                    <ListItem key={benefit}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Visa Types Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Types of Business Visas
          </Typography>
          <Grid container spacing={4}>
            {visaTypes.map((visa) => (
              <Grid item xs={12} md={6} key={visa.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                      {visa.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {visa.description}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Key Requirements:
                    </Typography>
                    <List>
                      {visa.requirements.map((req) => (
                        <ListItem key={req}>
                          <ListItemIcon>
                            <Assignment color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={req} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Application Process
          </Typography>
          <Grid container spacing={3}>
            {process.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Timeline color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Step {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Expert Guidance
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    Our team of experienced immigration lawyers specializes in UK business visas. We stay updated with the latest immigration rules and policies to provide you with accurate advice and support.
                  </Typography>
                  <Typography variant="body1">
                    We have helped numerous entrepreneurs and businesses successfully establish their presence in the United Kingdom.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <TrendingUp color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Business Success
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    We understand that immigration is just one part of your business journey. Our comprehensive support includes guidance on business planning, market entry, and compliance requirements.
                  </Typography>
                  <Typography variant="body1">
                    Our services include business plan review, endorsement application support, and ongoing guidance to ensure your business success in the UK.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Ready to Start Your UK Business Journey?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Contact us today for a free consultation and expert guidance
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default BusinessVisa; 